import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Descriptions, Tag, Button, Typography } from "antd";
import { ChevronLeft, ShoppingBag, Receipt } from "lucide-react";
import { format } from "date-fns";
import { getOrderDetail } from "services/ordersAndBookings.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import { orderStatusTag, formatDateTime, InfoCard, CustomerCard } from "../commons/orders.helpers";
import PageLoader from "Components/PageLoader/PageLoader";
import {
  getAppliedCodesText,
  getTotalLabel,
  isDiscountTotal,
} from "../commons/orderTotals.helpers";

const { Title, Text } = Typography;

function TotalsBreakdown({ order_totals }) {
  if (!order_totals?.length) return null;
  return (
    <div className="divide-y divide-border/50">
      {order_totals.map((t, i) => (
        <Row
          key={i}
          justify="space-between"
          align="middle"
          style={{ padding: "8px 0" }}
        >
          <Col>
            <Text
              type={t.type === "GRAND_TOTAL" ? undefined : "secondary"}
              strong={t.type === "GRAND_TOTAL"}
            >
              {getTotalLabel(t.type)}
            </Text>
          </Col>
          <Col>
            <Text
              type={isDiscountTotal(t.type) ? "success" : undefined}
              strong={t.type === "GRAND_TOTAL"}
              style={
                t.type === "GRAND_TOTAL"
                  ? { color: "var(--primary)" }
                  : undefined
              }
            >
              {isDiscountTotal(t.type) ? "- " : ""}
              {t.value?.currency} {Number(t.value?.amount ?? 0).toFixed(2)}
            </Text>
          </Col>
        </Row>
      ))}
    </div>
  );
}

function OrderDetail() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, errors } = await getOrderDetail(orderNumber);
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      setOrder(data);
    }
    load();
  }, [orderNumber]);

  if (loading)
    return (
      <PageLoader
        showHero={false}
        showTabs={false}
        showCards={false}
        rows={6}
      />
    );

  if (!order) {
    return (
      <Row justify="center" align="middle" style={{ padding: "96px 0" }}>
        <Col style={{ textAlign: "center" }}>
          <ShoppingBag className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-3" />
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Order not found
          </Text>
          <Button type="link" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Col>
      </Row>
    );
  }

  const cd = order.customer_details;

  return (
    <div className="space-y-6">
      <Button
        type="text"
        icon={<ChevronLeft className="h-4 w-4" />}
        onClick={() => navigate(-1)}
      >
        Back to Orders
      </Button>

      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 8]}>
        <Col xs={24} sm={16}>
          <Title level={3} style={{ margin: 0 }}>
            Order <Text code>{order.order_number}</Text>
          </Title>
          <Text type="secondary">
            {order.order_type?.replace(/_/g, " ")}
            {order.ordered_at &&
              ` · Placed on ${format(new Date(order.ordered_at), "PPP")}`}
          </Text>
        </Col>
        <Col xs={24} sm={8} style={{ textAlign: "right" }}>
          {orderStatusTag(order.order_status)}
        </Col>
      </Row>

      {/* Info cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <CustomerCard customer_details={cd} customer_id={order.customer_id} />
        </Col>

        <Col xs={24} md={8}>
          <InfoCard title="Order Info" icon={ShoppingBag}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ordered At">
                {formatDateTime(order.ordered_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Confirmed At">
                {order.confirmed_at ? formatDateTime(order.confirmed_at) : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {order.order_type?.replace(/_/g, " ") ?? "—"}
              </Descriptions.Item>
              {order.metadata?.vendor_name && (
                <Descriptions.Item label="Vendor">
                  {order.metadata.vendor_name}
                </Descriptions.Item>
              )}
              {order.metadata?.location_name && (
                <Descriptions.Item label="Venue">
                  {order.metadata.location_name}
                </Descriptions.Item>
              )}
            </Descriptions>
          </InfoCard>
        </Col>

        <Col xs={24} md={8}>
          <InfoCard title="Price Breakdown" icon={Receipt}>
            <TotalsBreakdown order_totals={order.order_totals} />
            {order.metadata?.platform_fee?.waived_units > 0 && (
              <Text type="secondary" className="block pt-2 text-xs">
                Platform fee covered by you: INR{" "}
                {Number(order.metadata.platform_fee.waived_amount ?? 0).toFixed(
                  2
                )}
              </Text>
            )}
            {getAppliedCodesText(order.metadata) && (
              <Text type="secondary" className="block pt-2 text-xs">
                Codes: {getAppliedCodesText(order.metadata)}
              </Text>
            )}
          </InfoCard>
        </Col>
      </Row>

      {/* Order items */}
      {order.order_items?.length > 0 && (
        <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
          <div className="px-6 py-4 border-b border-border/50">
            <Title level={5} style={{ margin: 0 }}>
              Items ({order.order_items.length})
            </Title>
          </div>
          <div className="divide-y divide-border/50">
            {order.order_items.map((item, i) => (
              <div key={item.order_item_id ?? i} className="px-6 py-4">
                <Row gutter={[16, 8]} align="top">
                  <Col xs={24} sm={14}>
                    <Text strong>
                      {item.product_details?.name ??
                        item.event_metadata?.title ??
                        "—"}
                    </Text>
                    {item.product_details?.description && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">
                          {item.product_details.description}
                        </Text>
                      </div>
                    )}
                    {item.event_metadata?.event_name && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">
                          Event: {item.event_metadata.event_name}
                          {item.event_metadata.event_type &&
                            ` · ${item.event_metadata.event_type}`}
                        </Text>
                      </div>
                    )}
                    {item.event_metadata?.start_time && (
                      <div style={{ marginTop: 2 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDateTime(item.event_metadata.start_time)}
                          {item.event_metadata.end_time &&
                            ` → ${formatDateTime(item.event_metadata.end_time)}`}
                        </Text>
                      </div>
                    )}
                    <Row gutter={16} style={{ marginTop: 6 }}>
                      {item.product_details?.product_type && (
                        <Col>
                          <Tag color="gold-inverse" style={{ fontSize: 11 }}>
                            {item.product_details.product_type}
                          </Tag>
                        </Col>
                      )}
                      {item.product_details?.product_domain && (
                        <Col>
                          <Tag color="blue-inverse" style={{ fontSize: 11 }}>
                            {item.product_details.product_domain}
                          </Tag>
                        </Col>
                      )}
                      {item.product_details?.usage_type && (
                        <Col>
                          <Tag color="cyan-inverse" style={{ fontSize: 11 }}>
                            {item.product_details.usage_type}
                          </Tag>
                        </Col>
                      )}
                    </Row>
                  </Col>
                  <Col xs={6} sm={4} style={{ textAlign: "center" }}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block" }}
                    >
                      Qty
                    </Text>
                    <Text strong>{item.quantity}</Text>
                  </Col>
                  <Col xs={6} sm={2} style={{ textAlign: "center" }}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block" }}
                    >
                      Unit
                    </Text>
                    <Text>{item.unit_price}</Text>
                  </Col>
                  <Col xs={6} sm={2} style={{ textAlign: "center" }}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block" }}
                    >
                      MRP
                    </Text>
                    <Text delete type="secondary">
                      {item.mrp}
                    </Text>
                  </Col>
                  <Col xs={6} sm={2} style={{ textAlign: "center" }}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block" }}
                    >
                      Total
                    </Text>
                    <Text strong style={{ color: "var(--primary)" }}>
                      {item.line_total}
                    </Text>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderDetail;
